// src/types/assetReports.ts
// TypeScript interfaces for qmLEO Asset Reports
// Created: 2025-02-01

import { VerificationStatus, DisposalMethod, FundingSource } from './asset';

// ============================================================================
// REPORT TYPES
// ============================================================================

export type AssetReportType = 
  | 'verification-status'
  | 'overdue'
  | 'inventory'
  | 'disposal'
  | 'value-summary';

// ============================================================================
// VERIFICATION STATUS REPORT
// ============================================================================

export interface VerificationStatusReportAsset {
  assetTag: string;
  name: string;
  categoryName: string;
  location: string;
  verificationStatus: VerificationStatus;
  lastVerifiedAt: string | null;
  lastVerifiedByName: string | null;
  verificationDueDate: string | null;
  currentValue: number | null;
}

export interface VerificationStatusReportSummary {
  total: number;
  verified: number;
  pending: number;
  overdue: number;
  new: number;
}

export interface VerificationStatusReport {
  reportType: 'verification-status';
  generatedAt: string;
  summary: VerificationStatusReportSummary;
  assets: VerificationStatusReportAsset[];
}

// ============================================================================
// OVERDUE ASSETS REPORT
// ============================================================================

export interface OverdueReportAsset {
  assetTag: string;
  name: string;
  categoryName: string;
  location: string;
  verificationDueDate: string | null;
  daysOverdue: number;
  lastVerifiedAt: string | null;
  lastVerifiedByName: string | null;
  currentValue: number | null;
}

export interface OverdueReportSummary {
  totalOverdue: number;
  totalValue: number;
}

export interface OverdueReport {
  reportType: 'overdue';
  generatedAt: string;
  summary: OverdueReportSummary;
  assets: OverdueReportAsset[];
}

// ============================================================================
// ASSET INVENTORY REPORT
// ============================================================================

export interface InventoryReportAsset {
  assetTag: string;
  name: string;
  description: string | null;
  categoryName: string;
  location: string;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  purchaseDate: string | null;
  purchaseValue: number | null;
  currentValue: number | null;
  fundingSource: FundingSource | null;
  grantId: string | null;
  status: string;
  verificationStatus: VerificationStatus;
  lastVerifiedAt: string | null;
}

export interface InventoryReportSummary {
  totalAssets: number;
  activeAssets: number;
  disposedAssets: number;
  totalPurchaseValue: number;
  totalCurrentValue: number;
}

export interface InventoryReport {
  reportType: 'inventory';
  generatedAt: string;
  summary: InventoryReportSummary;
  assets: InventoryReportAsset[];
}

// ============================================================================
// DISPOSAL REPORT
// ============================================================================

export interface DisposalReportAsset {
  assetTag: string;
  name: string;
  categoryName: string;
  serialNumber: string | null;
  disposedAt: string;
  disposalMethod: DisposalMethod;
  disposedTo: string | null;
  disposalAmount: number | null;
  disposalAuthorizedBy: string;
  disposalReference: string | null;
  disposalNotes: string | null;
  purchaseValue: number | null;
  currentValue: number | null;
}

export interface DisposalByMethod {
  method: string;
  count: number;
  totalValue: number;
  disposalAmount: number;
}

export interface DisposalReportSummary {
  totalDisposed: number;
  totalOriginalValue: number;
  totalDisposalAmount: number;
  byMethod: DisposalByMethod[];
}

export interface DisposalReport {
  reportType: 'disposal';
  generatedAt: string;
  dateRange: {
    startDate?: string;
    endDate?: string;
  };
  summary: DisposalReportSummary;
  assets: DisposalReportAsset[];
}

// ============================================================================
// VALUE SUMMARY REPORT
// ============================================================================

export interface ValueByCategory {
  category: string;
  count: number;
  purchaseValue: number;
  currentValue: number;
}

export interface ValueByFundingSource {
  fundingSource: string;
  count: number;
  purchaseValue: number;
  currentValue: number;
}

export interface ValueSummaryTotals {
  totalAssets: number;
  totalPurchaseValue: number;
  totalCurrentValue: number;
  depreciation: number;
}

export interface ValueSummaryReport {
  reportType: 'value-summary';
  generatedAt: string;
  totals: ValueSummaryTotals;
  byCategory: ValueByCategory[];
  byFundingSource: ValueByFundingSource[];
}

// ============================================================================
// REPORT FILTERS
// ============================================================================

export interface AssetReportFilters {
  categoryId?: string;
  startDate?: string;
  endDate?: string;
  includeDisposed?: boolean;
  format?: 'json' | 'csv';
}

// ============================================================================
// LABELS
// ============================================================================

export const REPORT_TYPE_LABELS: Record<AssetReportType, string> = {
  'verification-status': 'Verification Status',
  'overdue': 'Overdue Assets',
  'inventory': 'Asset Inventory',
  'disposal': 'Disposal Report',
  'value-summary': 'Value Summary',
};

export const REPORT_TYPE_DESCRIPTIONS: Record<AssetReportType, string> = {
  'verification-status': 'All assets with their current verification status',
  'overdue': 'Assets that are past their verification due date',
  'inventory': 'Complete asset listing for auditors',
  'disposal': 'Disposed assets within a date range',
  'value-summary': 'Asset values by category and funding source',
};
